/**
 * Job lifecycle. Appendix C.2 and §6.8 `JOB-005a`.
 *
 * > `draft → ordered → sent → acknowledged → in_production → shipped → received → qc → ready →
 * > collected`
 * >
 * > Branches: `qc → qc_failed → remake_raised` · any → `on_hold` → back · `sent` with no ack past
 * > SLA → `chasing` → `acknowledged` · `ordered|sent → cancelled`. **`ready` is unreachable except
 * > from a passed `qc`.** `collected` is terminal and triggers revenue recognition and warranty
 * > start.
 *
 * ---
 *
 * **The sentence in bold is release gate 4, and `CLAUDE.md` lists it among the things that must be
 * "impossible, not discouraged".**
 *
 * A transition table alone does not make it impossible. `qc → ready` being the only edge into
 * `ready` says a job must pass *through* the qc state; it does not say the check *passed*. A job
 * that entered `qc`, failed, and was moved to `ready` by a hurried counter would satisfy the table
 * and violate `JOB-005a`, and the customer collects glasses nobody verified.
 *
 * So the guard here takes the **quality check record** rather than only the two states, and there is
 * no overload that omits it. `JOB-005a`: "A QC failure creates a remake, not a collection."
 */
export const JOB_STATUSES = [
    'draft',
    'ordered',
    'sent',
    'chasing',
    'acknowledged',
    'in_production',
    'shipped',
    'received',
    'qc',
    'qc_failed',
    'remake_raised',
    'ready',
    'collected',
    'on_hold',
    'cancelled',
];
/**
 * Appendix C.2 as a table.
 *
 * `on_hold` is reachable from every non-terminal state ("any → `on_hold` → back") and returns to the
 * state it came from, which a flat table cannot express: the return edge depends on history. So
 * `on_hold` lists the states it can return to, and `resumeJob` below is what enforces that it goes
 * back where it came from rather than anywhere on that list.
 */
export const JOB_TRANSITIONS = {
    draft: ['ordered', 'cancelled', 'on_hold'],
    ordered: ['sent', 'cancelled', 'on_hold'],
    // "sent with no ack past SLA → chasing → acknowledged"
    sent: ['acknowledged', 'chasing', 'cancelled', 'on_hold'],
    chasing: ['acknowledged', 'on_hold'],
    acknowledged: ['in_production', 'on_hold'],
    in_production: ['shipped', 'on_hold'],
    shipped: ['received', 'on_hold'],
    received: ['qc', 'on_hold'],
    // The only edge into `ready` in the whole table, and it is still not sufficient on its own.
    qc: ['ready', 'qc_failed', 'on_hold'],
    qc_failed: ['remake_raised', 'on_hold'],
    // A remake raises a new job (C.3); this one does not continue.
    remake_raised: [],
    ready: ['collected', 'on_hold'],
    // Terminal. "triggers revenue recognition and warranty start."
    collected: [],
    cancelled: [],
    on_hold: [
        'draft',
        'ordered',
        'sent',
        'chasing',
        'acknowledged',
        'in_production',
        'shipped',
        'received',
        'qc',
        'qc_failed',
        'ready',
    ],
};
export class JobTransitionError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'JobTransitionError';
    }
}
/**
 * Whether a job may move, and the only function that answers it.
 *
 * Takes the quality check because the answer depends on it. A signature that omitted it would let a
 * caller ask the cheaper question by accident, which is precisely how a mandatory gate becomes an
 * optional one.
 *
 * Returns a reason rather than a boolean, because Appendix F's `QC_NOT_PASSED` has to reach the
 * caller and gate 4 tests for it by name.
 */
export function checkJobTransition(from, to, context) {
    const edges = JOB_TRANSITIONS[from];
    if (!edges?.includes(to)) {
        return {
            allowed: false,
            code: 'JOB_TRANSITION_INVALID',
            message: `A job cannot go from ${from} to ${to}.`,
        };
    }
    if (to !== 'ready')
        return { allowed: true };
    // Appendix C.2: "`ready` is unreachable except from a passed `qc`." Both halves are checked, and
    // the second is the one a table cannot express.
    if (from !== 'qc') {
        return {
            allowed: false,
            code: 'QC_NOT_PASSED',
            message: `A job reaches ready only from a completed quality check, not from ${from}.`,
        };
    }
    if (!context.qualityCheck) {
        return {
            allowed: false,
            code: 'QC_NOT_PASSED',
            message: 'This job has no quality check recorded. JOB-005a makes the check a mandatory gated step before a customer is told their glasses are ready.',
        };
    }
    if (!context.qualityCheck.passed) {
        return {
            allowed: false,
            code: 'QC_NOT_PASSED',
            message: `The quality check on this job failed${context.qualityCheck.failureReason === undefined ? '' : `: ${context.qualityCheck.failureReason}`}. A QC failure creates a remake, not a collection.`,
        };
    }
    return { allowed: true };
}
/**
 * The same question, as a throw, for a call site that has nothing useful to do with a refusal.
 *
 * Both exist because `JOB-005a` is a gate and a gate that is easy to check incorrectly is not one.
 */
export function assertJobTransition(from, to, context) {
    const verdict = checkJobTransition(from, to, context);
    if (!verdict.allowed)
        throw new JobTransitionError(verdict.code, verdict.message);
}
/**
 * `on_hold` returns to where it came from. Appendix C.2: "any → `on_hold` → **back**".
 *
 * The table cannot say this, because the destination is history rather than structure. Held
 * separately so a job coming off hold cannot be routed anywhere else on the way: resuming from hold
 * into `ready` would be a path around the QC gate that the table permits and the requirement does
 * not.
 */
export function resumeJob(heldFrom) {
    if (heldFrom === 'on_hold') {
        throw new JobTransitionError('JOB_TRANSITION_INVALID', 'A job on hold cannot have been held from on_hold.');
    }
    return heldFrom;
}
/** A quality check is only well formed if a failure says why. C.3 assigns cause from it. */
export function validateQualityCheck(check) {
    if (!check.passed && (check.failureReason === undefined || check.failureReason.trim() === '')) {
        throw new JobTransitionError('QC_REASON_REQUIRED', 'A failed quality check must record why. The remake it raises assigns cause from it (Appendix C.3).');
    }
    if (check.checkedByStaffId.trim() === '') {
        throw new JobTransitionError('QC_CHECKER_REQUIRED', 'A quality check records who checked. JOB-005a is a person looking at the glasses, never the system.');
    }
}
//# sourceMappingURL=job.js.map